import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import EnableProfileModal, {
  ModalHeading,
  ModalLinkText,
  ModalLinkUrl,
  ModalSubheading,
} from '@components/EnableProfileModal';
import { useCurrentUser, useEventTracking, useForumPost } from '@hooks';
import { TrackingLocation } from '@tracking';
import { ForumPost, NewForumPost } from '@utils/backend-api/forumPosts';
import {
  emptyPollAttributes,
  isStandalonePollPost,
  pollToAttributes,
} from '@utils/backend-api/polls';

import ForumPanel from '../ForumPanel';
import { ForumTypeRequest } from '../NewForumPostButton';
import CreatedCommentSuccess from './CreatedCommentSuccess';
import NewForumPostForm, {
  QUICK_TAG_FUNDING,
  QUICK_TAG_JOBS,
  QUICK_TAG_LAUNCH,
} from './NewForumPostForm';
import styles from './styles.module.scss';

const INITIAL_VALUES: NewForumPost = {
  title: '',
  body: '',
  external_url: '',
  entity_tags: [],
  email_digest_enabled: true,
  job_tag: false,
  product_tag: false,
  funding_tag: false,
  is_anonymous: false,
};

function getInitialValues(forumPost: ForumPost, id?: string, isPoll?: boolean) {
  if (typeof id !== 'undefined') {
    const tagList = forumPost?.entity_tags?.map((e) => e.label) || [];
    return forumPost
      ? {
          id: forumPost?.id,
          title: forumPost?.title,
          body: forumPost?.comment?.body,
          external_url: forumPost?.external_url,
          entity_tags: forumPost?.entity_tags,
          email_digest_enabled: forumPost?.email_digest_enabled,
          job_tag: tagList.includes(QUICK_TAG_JOBS),
          product_tag: tagList.includes(QUICK_TAG_LAUNCH),
          funding_tag: tagList.includes(QUICK_TAG_FUNDING),
          poll_attributes: forumPost.poll ? pollToAttributes(forumPost.poll) : undefined,
          remove_poll: false,
        }
      : undefined;
  }
  return isPoll ? { ...INITIAL_VALUES, poll_attributes: emptyPollAttributes() } : INITIAL_VALUES;
}

export default function EditForumPostPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const { forumPost } = useForumPost(params.id);
  const [createdForumPost, setCreatedForumPost] = useState<ForumPost | undefined>();
  // Polls can be created, and later edited until a vote is cast (staff can always edit)
  const isNewPoll = !params.id && searchParams.get('type') === ForumTypeRequest.Poll;
  const isEditingStandalonePoll = !!params.id && isStandalonePollPost(forumPost || {});
  const isPoll = isNewPoll || isEditingStandalonePoll;
  const initialValues = useMemo(
    () => getInitialValues(forumPost, params.id, isPoll),
    [forumPost, params.id, isPoll]
  );
  const { Track } = useEventTracking({
    location: TrackingLocation.EditForumPost,
  });

  const isAdmin = currentUser?.administrator?.is_active;
  const isManaging = isAdmin && !!params.id && forumPost?.comment?.user?.id !== currentUser.id;

  return (
    <Track>
      <div className={styles.NewNetworkPostPage}>
        <div className="container-padded container">
          <ForumPanel>
            {!!createdForumPost && <CreatedCommentSuccess forumPost={createdForumPost} />}
            {/* Keep in DOM in case user continues steps in EnableProfileModal */}
            <div style={{ display: createdForumPost ? 'none' : undefined }}>
              <h1 className={styles.heading}>
                {isManaging
                  ? isPoll
                    ? 'Manage poll'
                    : 'Manage conversation'
                  : params.id
                  ? isPoll
                    ? 'Edit poll'
                    : 'Edit conversation'
                  : isPoll
                  ? 'Create a poll'
                  : 'Start a new conversation'}
              </h1>
              {!!initialValues && (
                <NewForumPostForm
                  initialValues={initialValues}
                  isManaging={isManaging}
                  isPoll={isPoll}
                  existingPoll={params.id ? forumPost?.poll : undefined}
                  onCreateSuccess={(forumPost) => setCreatedForumPost(forumPost)}
                />
              )}
            </div>
          </ForumPanel>
        </div>
        {!currentUser?.is_subscriber && (
          <EnableProfileModal
            isLoggedIn={!!currentUser}
            isSubscriber={false}
            modalContent={{
              heading: ModalHeading.Lead,
              subheading: ModalSubheading.ForumPostLead,
              linkText: ModalLinkText.Lead,
              linkUrl: ModalLinkUrl.Lead,
            }}
          />
        )}
      </div>
    </Track>
  );
}
